/* eslint-disable max-len, object-curly-newline, react/jsx-one-expression-per-line, newline-per-chained-call, react/jsx-wrap-multilines, implicit-arrow-linebreak, function-paren-newline, operator-linebreak, react/jsx-curly-newline */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import Switch from '@mui/material/Switch';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Search from '@mui/icons-material/Search';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import RestoreFromTrashOutlined from '@mui/icons-material/RestoreFromTrashOutlined';
import CustomTable from '../../components/table/CustomTable';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

/*
 * Settings → Chart Abstraction. Org-wide on/off master switch + a per-member
 * grid granting Chart Tracking (rep) and/or QA Review access. Backed by
 * /workspace/:id/chart-abstraction/settings + /access/:memberId.
 */

const TABLE_HEADERS = [
  { label: 'Member', colSpan: 2 },
  { label: 'Designation', colSpan: 1 },
  { label: 'Chart Tracking', colSpan: 1 },
  { label: 'QA Review', colSpan: 1 },
];

const initials = (name) => name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

function ChartAbstraction() {
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace } = useContext(UserContext);
  const workspaceId = activeWorkspace?.id;

  const [enabled, setEnabled] = useState(false);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingFeature, setSavingFeature] = useState(false);
  const [severity, setSeverity] = useState({ minor: 5, major: 15, critical: 30 });
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let active = true;
    (async () => {
      setLoading(true);
      const res = await sendRequest({ method: 'get', url: API.CHART_ABSTRACTION.settings(workspaceId) });
      if (!active) return;
      if (res.success) {
        setEnabled(!!res.data.enabled);
        setMembers(res.data.members || []);
        if (res.data.severityPoints) setSeverity(res.data.severityPoints);
        setCategories(res.data.errorCategories || []);
      } else {
        displayNotification({ message: res.data?.error || 'Failed to load Chart Abstraction settings', severity: 'error' });
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [workspaceId]);

  const saveSeverity = async (next) => {
    setSeverity(next);
    const res = await sendRequest({ method: 'patch', url: API.CHART_ABSTRACTION.scoring(workspaceId), data: next });
    if (res.success && res.data?.severityPoints) {
      setSeverity(res.data.severityPoints);
    } else if (!res.success) {
      displayNotification({ message: res.data?.error || 'Could not update scoring', severity: 'error' });
    }
  };

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name || addingCategory) return;
    setAddingCategory(true);
    const res = await sendRequest({ method: 'post', url: API.CHART_ABSTRACTION.errorCategories(workspaceId), data: { name } });
    setAddingCategory(false);
    if (res.success && res.data?.category) {
      setCategories((prev) => {
        const rest = prev.filter((c) => c.id !== res.data.category.id);
        return [...rest, res.data.category].sort((a, b) => a.name.localeCompare(b.name));
      });
      setNewCategory('');
      displayNotification({ message: `Added error category "${name}"`, severity: 'success' });
    } else {
      displayNotification({ message: res.data?.error || 'Could not add category', severity: 'error' });
    }
  };

  const setCategoryActive = async (category, isActive) => {
    setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, isActive } : c)));
    const url = API.CHART_ABSTRACTION.errorCategory(workspaceId, category.id);
    // Re-enable via PATCH; "remove" is a soft-disable (DELETE) so history survives.
    const res = isActive
      ? await sendRequest({ method: 'patch', url, data: { isActive: true } })
      : await sendRequest({ method: 'delete', url });
    if (!res.success) {
      setCategories((prev) => prev.map((c) => (c.id === category.id ? { ...c, isActive: !isActive } : c)));
      displayNotification({ message: res.data?.error || 'Could not update category', severity: 'error' });
    }
  };

  const toggleFeature = async () => {
    if (savingFeature) return;
    const next = !enabled;
    setEnabled(next); // optimistic
    setSavingFeature(true);
    const res = await sendRequest({ method: 'patch', url: API.CHART_ABSTRACTION.settings(workspaceId), data: { enabled: next } });
    setSavingFeature(false);
    if (res.success) {
      displayNotification({ message: `Chart Abstraction ${next ? 'enabled' : 'turned off'} for your organization`, severity: next ? 'success' : 'info' });
    } else {
      setEnabled(!next); // revert
      displayNotification({ message: res.data?.error || 'Could not update the setting', severity: 'error' });
    }
  };

  const toggleAccess = async (memberId, key) => {
    const target = members.find((m) => m.memberId === memberId);
    if (!target) return;
    const nextVal = !target[key];
    setMembers((prev) => prev.map((m) => (m.memberId === memberId ? { ...m, [key]: nextVal } : m))); // optimistic
    const res = await sendRequest({ method: 'patch', url: API.CHART_ABSTRACTION.access(workspaceId, memberId), data: { [key]: nextVal } });
    if (res.success) {
      displayNotification({
        message: `${target.name} ${nextVal ? 'can now access' : 'no longer has access to'} ${key === 'chartTracking' ? 'Chart Tracking' : 'QA Review'}`,
        severity: 'success',
      });
    } else {
      setMembers((prev) => prev.map((m) => (m.memberId === memberId ? { ...m, [key]: !nextVal } : m))); // revert
      displayNotification({ message: res.data?.error || 'Could not update access', severity: 'error' });
    }
  };

  const chartCount = members.filter((m) => m.chartTracking).length;
  const qaCount = members.filter((m) => m.qaReview).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => m.name.toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q));
  }, [members, search]);

  const items = filtered.map((m) => ({
    member: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: '50%', background: 'var(--blue-7, #D8E6FC)', color: 'var(--primary, #213D69)', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
          {initials(m.name)}
        </span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>{m.name}</span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--dark-3, #546077)' }}>{m.email}</span>
        </span>
      </div>
    ),
    designation: <span style={{ fontSize: 14, color: 'var(--dark-2, #293855)' }}>{m.designation}</span>,
    chartTracking: <Switch checked={!!m.chartTracking} disabled={!enabled} onChange={() => toggleAccess(m.memberId, 'chartTracking')} color="primary" />,
    qaReview: <Switch checked={!!m.qaReview} disabled={!enabled} onChange={() => toggleAccess(m.memberId, 'qaReview')} color="primary" />,
  }));

  return (
    <div style={{ padding: '20px 4px', fontFamily: 'Inter, sans-serif' }}>
      {/* Org master switch */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '16px 18px', border: '1px solid var(--dark-6, #E0E1E6)', borderRadius: 10, marginBottom: 22, background: enabled ? '#fff' : 'var(--dark-10, #FAFAFA)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)' }}>Chart Abstraction</span>
            <span style={{ fontSize: 12, fontWeight: 600, borderRadius: 20, padding: '2px 8px', ...(enabled ? { background: '#E7F6E6', color: 'var(--green-1, #256D23)' } : { background: 'var(--dark-7, #ECECF0)', color: 'var(--dark-3, #546077)' }) }}>
              {enabled ? 'On' : 'Off'}
            </span>
          </div>
          <p style={{ fontSize: 14, color: 'var(--dark-3, #546077)', margin: '4px 0 0', maxWidth: 640 }}>
            Turn the chart tracking &amp; QA review workflow on or off for your entire organization. When off, no one can log chart time or review — regardless of individual access below.
          </p>
        </div>
        <Switch checked={enabled} disabled={loading || savingFeature} onChange={toggleFeature} color="primary" />
      </div>

      {/* Member access */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)', margin: 0 }}>Member access</h4>
          <p style={{ fontSize: 14, color: 'var(--dark-3, #546077)', margin: '4px 0 0', maxWidth: 560 }}>
            Grant members access to the Chart Tracking timer and/or the QA Review queue.
          </p>
        </div>
        <TextField
          size="small"
          placeholder="Search members"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><Search style={{ fontSize: 18, color: 'var(--dark-4, #7D8698)' }} /></InputAdornment>) }}
          sx={{ width: 240 }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 8, marginBottom: 8, background: 'var(--dark-9, #FBFBFC)', border: '1px solid var(--dark-6, #E0E1E6)' }}>
        <InfoOutlined style={{ fontSize: 18, color: 'var(--primary, #213D69)', flexShrink: 0, marginTop: 1 }} />
        <p style={{ fontSize: 14, color: 'var(--dark-2, #293855)', margin: 0 }}>
          <strong>Chart Tracking</strong>
          {' lets a member log time on charts in the desktop timer. '}
          <strong>QA Review</strong>
          {' lets them approve or deny completed charts. '}
          <span style={{ color: 'var(--dark-3, #546077)' }}>{`${chartCount} with Chart Tracking · ${qaCount} with QA Review`}</span>
        </p>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}><CircularProgress size={26} /></div>
      ) : (
        <CustomTable headers={TABLE_HEADERS} items={items} noItemText="No members match your search." />
      )}

      {/* QA scoring */}
      <div style={{ marginTop: 34 }}>
        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)', margin: 0 }}>QA scoring</h4>
        <p style={{ fontSize: 14, color: 'var(--dark-3, #546077)', margin: '4px 0 14px', maxWidth: 640 }}>
          When QA denies a chart, its score starts at 100% and each logged error deducts points by severity (the score is floored at 0). Accepted charts always score 100%.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
          {[
            { key: 'minor', label: 'Minor', color: '#256D23' },
            { key: 'major', label: 'Major', color: '#B3650F' },
            { key: 'critical', label: 'Critical', color: '#CC4525' },
          ].map((s) => (
            <div key={s.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: s.color }}>{s.label}</span>
              <TextField
                type="number"
                size="small"
                disabled={!enabled}
                value={severity[s.key]}
                onChange={(e) => setSeverity((prev) => ({ ...prev, [s.key]: e.target.value }))}
                onBlur={(e) => saveSeverity({ ...severity, [s.key]: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                InputProps={{ endAdornment: <InputAdornment position="end">pts</InputAdornment>, inputProps: { min: 0 } }}
                sx={{ width: 130 }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Error categories (Error Documentation) */}
      <div style={{ marginTop: 34 }}>
        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--dark-1, #0E1E40)', margin: 0 }}>Error categories</h4>
        <p style={{ fontSize: 14, color: 'var(--dark-3, #546077)', margin: '4px 0 14px', maxWidth: 640 }}>
          The list QA reviewers choose from when documenting the errors on a denied chart. These power the &quot;common error types&quot; report. Removing a category hides it from new denials but keeps it on past records.
        </p>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, maxWidth: 460 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="e.g. Coding error, Missing documentation"
            value={newCategory}
            disabled={!enabled}
            onChange={(e) => setNewCategory(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }}
          />
          <Button variant="contained" disabled={!enabled || !newCategory.trim() || addingCategory} startIcon={<AddCircleOutline />} onClick={addCategory} sx={{ textTransform: 'none', backgroundColor: 'var(--primary, #213D69)', whiteSpace: 'nowrap', '&:hover': { backgroundColor: '#1a3156' } }}>Add</Button>
        </div>
        {categories.length === 0 ? (
          <div style={{ fontSize: 14, color: 'var(--dark-4, #7D8698)' }}>No categories yet. Add a few so QA can document errors.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {categories.map((c) => (
              <Chip
                key={c.id}
                label={c.name}
                variant={c.isActive ? 'filled' : 'outlined'}
                onDelete={enabled ? () => setCategoryActive(c, !c.isActive) : undefined}
                deleteIcon={c.isActive ? <DeleteOutline /> : <RestoreFromTrashOutlined />}
                sx={{
                  opacity: c.isActive ? 1 : 0.55,
                  backgroundColor: c.isActive ? 'var(--blue-7, #D8E6FC)' : 'transparent',
                  color: 'var(--primary, #213D69)',
                  fontWeight: 500,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChartAbstraction;
